import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import type {
  AdvancePaymentReceipt,
  AdvancePaymentsListRequest,
  PagedResults,
} from '@/shared/types/billing';

/**
 * Patient advance-payment receipts list. Powers the Receipts sub-tab of
 * Patient Billing (PROD-733).
 *
 * Wire (verified against swagger 2026-05-15 +
 * Flutter `patient_billing_data_source_impl.dart:81`):
 *
 *   POST /Billing/Payment/AdvancePaymentReceipts?PatientId={id}
 *        body: AdvancePaymentsListRequest
 *        → PagedResults<AdvancePaymentReceipt>
 *
 * Unlike `SearchInvoices`, this endpoint DOES wrap the response in a
 * `PagedResults` envelope with `totalNumberOfRecords` + `totalNumberOfPages`.
 * The pager UI uses those to render "Page X of Y".
 *
 * **Casing-drift fix 2026-05-20** (Tier-3 patient_billing PROD-850, see
 * `docs/CONVERSIONS/patient-billing-advance-flow/CONVERSION_NOTES.md`
 * §7.B): query-param is `?PatientId=` (capital P) verbatim from Flutter
 * (`patient_billing_data_source_impl.dart:81`). Prior React code shipped
 * `?patientId=` (lowercase); .NET model binder verified case-insensitive
 * on this param (both casings 200 with identical envelope on 2026-05-20
 * dev curl), so the change is wire-neutral — alignment is per the
 * magic-strings-verbatim rule, not because lowercase fails.
 */

export interface UsePatientAdvanceReceiptsParams {
  patientId: string | null | undefined;
  pageNumber?: number;
  pageSize?: number;
  query?: string;
  startDate?: string | null;
  endDate?: string | null;
  receiptId?: string | null;
  paymentMethod?: string[] | null;
}

export function usePatientAdvanceReceipts(
  params: UsePatientAdvanceReceiptsParams,
) {
  const body: AdvancePaymentsListRequest = {
    query: params.query ?? null,
    startDate: params.startDate ?? null,
    endDate: params.endDate ?? null,
    receiptId: params.receiptId ?? null,
    paymentMethod: params.paymentMethod ?? null,
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 20,
  };

  return useQuery({
    queryKey: [
      'patient-advance-receipts',
      params.patientId,
      body.pageNumber,
      body.pageSize,
      body.query,
      body.startDate,
      body.endDate,
      body.receiptId,
      body.paymentMethod,
    ],
    queryFn: ({ signal }) => {
      // `PatientId` (capital P) verbatim from Flutter — see file header
      // "Casing-drift fix 2026-05-20".
      const qs = new URLSearchParams({ PatientId: params.patientId ?? '' });
      return post<PagedResults<AdvancePaymentReceipt>>(
        `${BILLING_BASE}/Payment/AdvancePaymentReceipts?${qs.toString()}`,
        body,
        { signal },
      );
    },
    enabled: !!params.patientId,
    placeholderData: (prev) => prev,
  });
}
